import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { toastApiError } from "@/api/errors";
import type { KnowledgeBase, Team } from "@/api/types";
import { useAuth } from "@/auth/useAuth";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PermissionsTab } from "@/features/knowledge-bases/components/PermissionsTab";
import {
  useCreateKb,
  useUpdateKb,
} from "@/features/knowledge-bases/hooks/useKbs";
import { useTeams } from "@/features/teams/hooks/useTeams";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  kb?: KnowledgeBase | null;
}

export function KbFormDialog({ open, onOpenChange, kb }: Props) {
  const { t } = useTranslation("common");
  const { t: tk } = useTranslation("knowledgeBases");
  const isEdit = Boolean(kb);
  const create = useCreateKb();
  const update = useUpdateKb(kb?.id ?? "");
  const { data: teams } = useTeams({ include_inactive: false });
  const { isAdmin, memberships } = useAuth();

  // Times onde o user pode criar KB: admin ve tudo; demais usuarios so
  // veem times em que tem role manager OU creator (espelha a regra do
  // backend em policies.can(Action.KNOWLEDGE_BASE_CREATED)).
  const eligibleTeams = useMemo<Team[]>(() => {
    if (!teams) return [];
    if (isAdmin) return teams;
    const elevatedTeamIds = new Set(
      memberships
        .filter((m) => m.role === "manager" || m.role === "creator")
        .map((m) => m.team.id),
    );
    return teams.filter((tm) => elevatedTeamIds.has(tm.id));
  }, [teams, isAdmin, memberships]);

  const [name, setName] = useState("");
  const [shortDescription, setShortDescription] = useState("");
  const [description, setDescription] = useState("");
  const [ownerTeamId, setOwnerTeamId] = useState("");
  // Etapa do wizard de criacao (1=dados, 2=permissoes). Edit nao usa.
  const [step, setStep] = useState<1 | 2>(1);
  const [createdKb, setCreatedKb] = useState<KnowledgeBase | null>(null);

  useEffect(() => {
    if (!open) return;
    setStep(1);
    setCreatedKb(null);
    if (kb) {
      setName(kb.name);
      setShortDescription(kb.short_description ?? "");
      setDescription(kb.description ?? "");
      setOwnerTeamId(kb.owner_team_id);
    } else {
      setName("");
      setShortDescription("");
      setDescription("");
      setOwnerTeamId(eligibleTeams[0]?.id ?? "");
    }
  }, [open, kb, eligibleTeams]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      if (isEdit && kb) {
        await update.mutateAsync({
          name,
          short_description: shortDescription || undefined,
          description: description || undefined,
        });
        toast.success(tk("form.toasts.updated"));
        onOpenChange(false);
      } else {
        const created = await create.mutateAsync({
          name,
          owner_team_id: ownerTeamId,
          short_description: shortDescription || undefined,
          description: description || undefined,
        });
        toast.success(tk("form.toasts.created"));
        // Avanca para a etapa de permissoes em vez de fechar.
        setCreatedKb(created);
        setStep(2);
      }
    } catch (err) {
      toastApiError(err);
    }
  }

  const pending = create.isPending || update.isPending;
  const isPermissionsStep = !isEdit && step === 2 && createdKb;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isEdit ? tk("form.titleEdit") : tk("form.titleNew")}
          </DialogTitle>
          <DialogDescription>
            {isPermissionsStep
              ? tk("form.permissionsStep.hint")
              : tk("form.description")}
          </DialogDescription>
        </DialogHeader>

        {isPermissionsStep ? (
          <div className="space-y-4">
            <PermissionsTab kb={createdKb} />
            <DialogFooter>
              <Button type="button" onClick={() => onOpenChange(false)}>
                {tk("form.actions.finish")}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="kb-name">{tk("form.labels.name")}</Label>
              <Input
                id="kb-name"
                required
                minLength={2}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="kb-short-desc">
                {tk("form.labels.shortDescription")}
              </Label>
              <Textarea
                id="kb-short-desc"
                rows={2}
                maxLength={280}
                value={shortDescription}
                onChange={(e) => setShortDescription(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                {tk("form.labels.shortDescriptionHint")}
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="kb-desc">
                {tk("form.labels.longDescription")}
              </Label>
              <Textarea
                id="kb-desc"
                rows={4}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                {tk("form.labels.longDescriptionHint")}
              </p>
            </div>
            {!isEdit ? (
              <div className="space-y-2">
                <Label htmlFor="kb-team">{tk("form.labels.ownerTeam")}</Label>
                {eligibleTeams.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    {tk("form.labels.noEligibleTeam")}
                  </p>
                ) : (
                  <>
                    <select
                      id="kb-team"
                      className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                      value={ownerTeamId}
                      onChange={(e) => setOwnerTeamId(e.target.value)}
                      required
                    >
                      <option value="" disabled>
                        {tk("form.labels.selectTeam")}
                      </option>
                      {eligibleTeams.map((tm) => (
                        <option key={tm.id} value={tm.id}>
                          {tm.name}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-muted-foreground">
                      {tk("form.labels.teamFilterHint")}
                    </p>
                  </>
                )}
              </div>
            ) : null}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                {t("actions.cancel")}
              </Button>
              <Button
                type="submit"
                disabled={
                  pending || name.length < 2 || (!isEdit && !ownerTeamId)
                }
              >
                {isEdit ? t("actions.save") : tk("form.actions.next")}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
